import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SectionHeading from "@/components/base/SectionHeading";
import VideoCard from "@/components/feature/VideoCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import HomeFeed from "@/pages/home/components/HomeFeed";
import CategoryShortcuts from "@/pages/home/components/CategoryShortcuts";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getHotVideos, getLatestVideos } from "@/utils/video";
import { videos } from "@/mocks/videos";

const stats = [
  { value: `${videos.length}`, label: "收录内容" },
  { value: "3", label: "内容分类" },
  { value: "6", label: "覆盖地区" },
  { value: "每日", label: "持续更新" },
];

export default function HomePage() {
  usePageMeta(
    "色色视频 - 多元高清视频内容聚合平台",
    "色色视频为用户提供丰富多元的视频资源，包括电影、短片和热门视频内容，方便在线浏览和发现最新精彩视频。",
    "色色视频,在线视频,高清视频,视频聚合,影视资源,在线视频平台",
  );

  const latest = getLatestVideos(8);
  const hot = getHotVideos(8);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="container-page pt-5 md:pt-7">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="inline-flex items-center gap-2 text-[11px] text-foreground-500 md:text-xs">
              <span className="flex h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
              内容聚合展示 · 精选焦点每日更新
            </p>
            <p className="font-label text-[10px] uppercase tracking-[0.18em] text-foreground-400">
              2026.10.17 · 内容索引
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-8 xl:col-span-9">
              <HeroCarousel />
            </div>
            <div className="lg:col-span-4 xl:col-span-3">
              <HomeFeed />
            </div>
          </div>

          <div className="mt-4 md:mt-5">
            <CategoryShortcuts />
          </div>
        </div>

        <section className="container-page py-9 md:py-12" aria-labelledby="latest-heading">
          <div id="latest-heading">
            <SectionHeading
              eyebrow="Latest Updates"
              title="最新视频"
              description="按收录时间倒序整理，包含电影、短片与热门纪录内容，点击卡片查看完整信息。"
              actionLabel="查看全部"
              actionTo="/category/all"
            />
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {latest.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-9 md:py-12" aria-labelledby="hot-heading">
          <div className="container-page">
            <div id="hot-heading">
              <SectionHeading
                eyebrow="Most Popular"
                title="热门视频"
                description="依据浏览量整理的站内热门内容，适合快速找到近期关注度最高的视频。"
                actionLabel="进入热门分类"
                actionTo="/category/hot"
              />
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {hot.map((video, index) => (
                <VideoCard key={video.id} video={video} rank={index + 1} />
              ))}
            </div>
          </div>
        </section>

        <section className="container-page py-9 md:py-12" aria-labelledby="intro-heading">
          <div className="overflow-hidden rounded-lg bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 px-5 py-8 md:px-10 md:py-10">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <p className="mb-2 inline-flex items-center gap-2 font-label text-[10px] font-semibold uppercase tracking-[0.2em] text-accent-300">
                  <span className="h-[2px] w-6 rounded-full bg-accent-400" aria-hidden="true" />
                  About the Site
                </p>
                <h2
                  id="intro-heading"
                  className="text-xl font-semibold leading-snug text-background-50 md:text-2xl"
                >
                  多元视频内容的聚合与索引
                </h2>
                <p className="mt-3 max-w-2xl text-xs leading-relaxed text-background-200 md:text-sm">
                  色色视频围绕电影、短片与热门内容建立分类体系，提供封面、时长、年份、地区与简介等信息索引，帮助用户在浏览与筛选中快速定位感兴趣的内容。本站为纯内容聚合展示平台，不提供在线播放、下载或会员服务。
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <Link
                    to="/about"
                    className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    了解站点定位
                    <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
                  </Link>
                  <Link
                    to="/search"
                    className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    <i className="ri-search-line text-sm" aria-hidden="true" />
                    前往搜索
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 lg:col-span-5">
                {stats.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-md border border-background-50/15 bg-background-50/[0.06] px-4 py-3"
                  >
                    <p className="font-heading text-xl font-semibold text-accent-300 md:text-2xl">
                      {item.value}
                    </p>
                    <p className="mt-1 text-[11px] text-background-300">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}